/**
 * The browser half of a WebAuthn ceremony: the authentication service hands
 * out options as JSON (binary fields base64url-encoded, as go-webauthn writes
 * them); these helpers turn them into what navigator.credentials expects, and
 * the credential the browser returns back into JSON for the service.
 *
 * The conversions are written out rather than left to
 * PublicKeyCredential.parseCreationOptionsFromJSON and toJSON(), which not
 * every browser an operator might use has yet.
 */

/** isWebAuthnAvailable reports whether this browser can use a security key. */
export function isWebAuthnAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
}

/** isCeremonyCancelled reports a ceremony the person dismissed or let time
 * out: the browser says only NotAllowedError, deliberately. */
export function isCeremonyCancelled(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

function fromBase64url(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function toBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface DescriptorJSON {
  type: PublicKeyCredentialType;
  id: string;
  transports?: AuthenticatorTransport[];
}

function descriptors(
  list: DescriptorJSON[] | undefined,
): PublicKeyCredentialDescriptor[] | undefined {
  return list?.map((d) => ({ ...d, id: fromBase64url(d.id) }));
}

interface CreationOptionsJSON {
  publicKey: Omit<
    PublicKeyCredentialCreationOptions,
    'challenge' | 'user' | 'excludeCredentials'
  > & {
    challenge: string;
    user: { id: string; name: string; displayName: string };
    excludeCredentials?: DescriptorJSON[];
  };
}

interface RequestOptionsJSON {
  publicKey: Omit<PublicKeyCredentialRequestOptions, 'challenge' | 'allowCredentials'> & {
    challenge: string;
    allowCredentials?: DescriptorJSON[];
  };
}

/** createCredential runs navigator.credentials.create with the service's
 * registration options and returns the new credential as JSON. */
export async function createCredential(options: unknown): Promise<unknown> {
  const { excludeCredentials, ...publicKey } = (options as CreationOptionsJSON).publicKey;
  const excluded = descriptors(excludeCredentials);
  const credential = await navigator.credentials.create({
    publicKey: {
      ...publicKey,
      challenge: fromBase64url(publicKey.challenge),
      user: { ...publicKey.user, id: fromBase64url(publicKey.user.id) },
      ...(excluded ? { excludeCredentials: excluded } : {}),
    },
  });
  if (!(credential instanceof PublicKeyCredential))
    throw new DOMException('no credential', 'NotAllowedError');
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: toBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    response: {
      clientDataJSON: toBase64url(response.clientDataJSON),
      attestationObject: toBase64url(response.attestationObject),
      transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

/** getAssertion runs navigator.credentials.get with the service's sign-in
 * options and returns the assertion as JSON. */
export async function getAssertion(options: unknown): Promise<unknown> {
  const { allowCredentials, ...publicKey } = (options as RequestOptionsJSON).publicKey;
  const allowed = descriptors(allowCredentials);
  const credential = await navigator.credentials.get({
    publicKey: {
      ...publicKey,
      challenge: fromBase64url(publicKey.challenge),
      ...(allowed ? { allowCredentials: allowed } : {}),
    },
  });
  if (!(credential instanceof PublicKeyCredential))
    throw new DOMException('no credential', 'NotAllowedError');
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: toBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    response: {
      clientDataJSON: toBase64url(response.clientDataJSON),
      authenticatorData: toBase64url(response.authenticatorData),
      signature: toBase64url(response.signature),
      ...(response.userHandle ? { userHandle: toBase64url(response.userHandle) } : {}),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}
